import React, { useState, useEffect } from "react";
import useDeviceType from "../../shared/hooks/useDeviceType";
import { DeviceTypes } from "../../shared/utils/enums";
import SideMenu from "./sideMenu";
import { CustomLayoutProps } from "../../interfaces/customLayout";
import { Menu, X, FileText } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useLocation } from "react-router-dom";
import { sideMenu, additionalPages } from "../../config/sideMenu";
import { getCategoriesConfig } from "../content/genioMD/actions";
import { store } from "../../redux/store";
import { setCategories, setLoadingCategories } from "../auth/login/reducer";
import { getTabs } from "../content/drugsAndDiseases/actions";

const CustomLayout: React.FC<CustomLayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.mobile;
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const isQARoute = location.pathname.includes('/qa');
  const noPaddingRoutes = ['/drugs-and-diseases'];

  // Fetch categories on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getCategoriesConfig();
        await getTabs();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const getCurrentPageInfo = () => {
    const currentPath = location.pathname;
    let currentPage = null;
    let breadcrumbs: string[] = [];

    // Check both sideMenu and additionalPages
    const allPages = [...sideMenu, ...additionalPages];

    // Find the current page and its parent
    for (const item of allPages) {
      if (
        item.route === currentPath || 
        (item.matchChildRoutes && currentPath.startsWith(item.route)) ||
        (item.route.includes(':id') && currentPath.startsWith(item.route.split(':')[0]))
      ) {
        currentPage = item;
        breadcrumbs = [item.label];
        break;
      }
      if (item.children) {
        const child = item.children.find(
          (child) => child.route === currentPath,
        );
        if (child) {
          currentPage = child;
          breadcrumbs = [item.label, child.label];
          break;
        }
      }
    }

    if (!currentPage && (currentPath.includes('/details/') || currentPath.includes('/info/'))){
      currentPage = {
        label: 'Details',
        route: currentPath,
        icon: FileText
      };
    }

    // If no match found in config, try to extract from path
    if (!currentPage) {
      const pathParts = currentPath.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        // Get the last part of the path
        let lastPart = pathParts[pathParts.length - 1];
        
        // Check if the last part contains a number after a hyphen
        const lastHyphenIndex = lastPart.lastIndexOf('-');
        if (lastHyphenIndex !== -1) {
          const afterHyphen = lastPart.slice(lastHyphenIndex + 1);
          // If the part after the last hyphen is a number, remove it
          if (!isNaN(Number(afterHyphen))) {
            lastPart = lastPart.slice(0, lastHyphenIndex);
          }
        }

        // Format the label
        const formattedLabel = lastPart
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        currentPage = {
          label: formattedLabel,
          route: currentPath,
          icon: null
        };
        breadcrumbs = [formattedLabel];
      }
    }

    return { currentPage, breadcrumbs };
  };

  const { currentPage, breadcrumbs } = getCurrentPageInfo();

  if (!currentUser) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <div
          className={`
            ${
              isMobile
                ? `fixed top-0 left-0 h-full w-72 bg-white dark:bg-black z-50 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}`
                : "bg-white dark:bg-black"
            }
          `}
        >
          <SideMenu onLinkClick={closeMenu} isCollapsed={!isMobile && isQARoute} />
        </div>

        {/* Desktop Header */}
        {!isMobile && (
          <div className={`fixed top-0 right-0 h-16 bg-white dark:bg-black border-b border-gray-100 dark:border-gray-900 px-6 flex items-center z-40 transition-all duration-300 ease-in-out ${isQARoute ? 'left-16' : 'left-72'}`}>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {currentPage?.icon && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-900">
                    <currentPage.icon size={18} className="text-blue-500" />
                  </div>
                )}
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {currentPage?.label || "InsightHub"}
                </h1>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Header */}
        {isMobile && (
          <div className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-black px-4 flex items-center justify-between z-40">
            <div className="flex items-center space-x-2">
              {currentPage?.icon && (
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-900">
                  <currentPage.icon size={18} className="text-blue-500" />
                </div>
              )}
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentPage?.label || "InsightHub"}
              </h1>
            </div>
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-400/40 transition-colors duration-200"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        )}

        {/* Mobile Overlay */}
        {isMobile && isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={closeMenu}
          />
        )}

        {/* Main Content */}
        <div
          className={`flex-1 ${isMobile ? "pt-16" :noPaddingRoutes.includes(location.pathname) ? "pt-0" : 'pt-16'} overflow-y-auto ${
            (noPaddingRoutes.includes(location.pathname) || isQARoute) ? '' : 'px-6'
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default CustomLayout;
